import raw from '../data/countries.json';

export type Lang = 'en' | 'es' | 'fr' | 'de' | 'pt' | 'it' | 'tr';

export interface Country {
  cca2: string; // ISO alpha-2,小写,统一主键
  cca3: string; // ISO alpha-3
  ccn3: string; // ISO numeric(用于 world-atlas 关联)
  names: Record<Lang, string>;
  region: string;
  subregion: string;
  capital: string;
  capitalLatlng: [number, number] | null;
  latlng: [number, number]; // [lat, lng] 国家中心
  area: number; // km²
  population: number;
  gdpPc: number; // 人均 GDP(现价美元);0 = 无数据(朝鲜 / 梵蒂冈)
  tempC: number | null; // 1991–2020 年均气温 °C(全国土面积均值)
  borders: string[]; // cca3 列表
  landlocked: boolean;
  hemisphere: 'N' | 'S';
  hasOutline: boolean; // 是否存在于 world-atlas 110m 轮廓(用于 Outline 出题池)
}

export const countries = raw as unknown as Country[];

const byCca3Map = new Map(countries.map((c) => [c.cca3, c]));
export function byCca3(cca3: string): Country | undefined {
  return byCca3Map.get(cca3);
}

const byCca2Map = new Map(countries.map((c) => [c.cca2, c]));
export function byCca2(cca2: string): Country | undefined {
  return byCca2Map.get(cca2.toLowerCase());
}

export function localName(c: Country, lang: Lang): string {
  return c.names[lang] ?? c.names.en;
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(new RegExp('[\\u0300-\\u036f]', 'g'), '')
    .trim();
}

/** 按本地化名搜索,用于自动补全 */
export function search(query: string, lang: Lang, limit = 8): Country[] {
  const q = normalize(query);
  if (!q) return [];
  const starts: Country[] = [];
  const contains: Country[] = [];
  for (const c of countries) {
    const n = normalize(localName(c, lang));
    if (n.startsWith(q)) starts.push(c);
    else if (n.includes(q)) contains.push(c);
  }
  return [...starts, ...contains].slice(0, limit);
}

/** 按本地化名精确匹配(忽略大小写/重音) */
export function findByName(name: string, lang: Lang): Country | undefined {
  const q = normalize(name);
  return countries.find((c) => normalize(localName(c, lang)) === q);
}

const R = 6371; // km
function toRad(d: number): number {
  return (d * Math.PI) / 180;
}

/** 两国中心间大圆距离(km) */
export function distanceKm(a: Country, b: Country): number {
  const [lat1, lon1] = a.latlng;
  const [lat2, lon2] = b.latlng;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(s)));
}

/** 从 a 指向 b 的方位角(度,0=正北,顺时针) */
export function bearing(a: Country, b: Country): number {
  const [lat1, lon1] = a.latlng;
  const [lat2, lon2] = b.latlng;
  const dLon = toRad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
  return (Math.atan2(y, x) * 180) / Math.PI;
}

export function flagSrc(c: Country): string {
  return `/assets/flags/${c.cca2}.svg`;
}

/** 紧凑数字:1.4B / 770K(本地化) */
export function compactNum(n: number, lang: Lang): string {
  return new Intl.NumberFormat(lang, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}
